/**
 * Telemedicine · Phase 1 · P1.UI Phase C/D — In-call floating controls bar.
 *
 * Figma node 6686:19938 (At-the-Meet) / 6684:19938 (Waiting). Both states
 * share the same chrome:
 *
 *   ┌──────────────────────────────────────────────────────────┐
 *   │  ┌────┐   ┌────┐   ┌────┐   ┌────┐                       │
 *   │  │mic │   │cam │   │flip│   │exit│   ← 48h pills, flex 1  │
 *   │  └────┘   └────┘   └────┘   └────┘                       │
 *   │   Mic     Câmera    Girar    Sair   ← white labels        │
 *   └──────────────────────────────────────────────────────────┘
 *      ▲ glass-dark wrapper #15151566, radius 24, padding 16
 *
 * Pill backgrounds:
 *   - mic / camera ON   → white      (#FFFFFF), inline soundwave / record dot
 *   - mic / camera OFF  → dune       (#353535), Tenn-orange diagonal slash
 *   - flip              → white      (always)
 *   - exit              → Tenn       (#DE5609)
 *
 * Icon set is shared with the lobby — the glyphs are exported from
 * `LobbyControlPill` so we only ever trace each Figma path once.
 */
import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import * as Haptics from 'expo-haptics';
import {
  MicGlyph,
  RecordVideoGlyph,
  CameraFlipGlyph,
  DiagonalSlash,
} from '@/components/video/lobby/LobbyControlPill';
import AnimatedSoundwave from '@/components/video/AnimatedSoundwave';

// Per-pill haptic intent. Toggle pills (mic/camera) use a "selection" tick so
// rapid on/off taps don't feel like jackhammer impacts. Flip is a one-shot
// action — light impact reads as "I switched something". Exit ends the call,
// so it gets a heavier impact to feel decisive.
type HapticKind = 'selection' | 'light' | 'medium';
function fireHaptic(kind: HapticKind) {
  try {
    if (kind === 'selection') {
      Haptics.selectionAsync();
    } else if (kind === 'light') {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    } else {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    }
  } catch {}
}

const WHITE = '#FFFFFF';
const DUNE = '#353535';
const INK = '#151515';
const TENN = '#DE5609';
const BLUE_HORIZON = '#669AB7';
const GLASS_BG = 'rgba(21, 21, 21, 0.4)'; // #15151566

interface IncallControlsBarProps {
  micEnabled: boolean;
  cameraEnabled: boolean;
  /** Disables the flip pill until the camera is on (matches lobby behavior). */
  canFlipCamera: boolean;
  onToggleMic: () => void;
  onToggleCamera: () => void;
  onSwitchCamera: () => void;
  onEnd: () => void;
  /** Live mic level 0..1 — drives the soundwave on the mic pill. */
  audioLevel?: number;
}

export default function IncallControlsBar({
  micEnabled,
  cameraEnabled,
  canFlipCamera,
  onToggleMic,
  onToggleCamera,
  onSwitchCamera,
  onEnd,
  audioLevel = 0,
}: IncallControlsBarProps) {
  return (
    <View style={styles.glass}>
      {/* Mic */}
      <Pill
        bg={micEnabled ? WHITE : DUNE}
        iconColor={micEnabled ? INK : WHITE}
        showSlash={!micEnabled}
        label="Mic"
        onPress={onToggleMic}
        haptic="selection"
        accessibilityState={{ selected: micEnabled }}
        accessibilityLabel={`Microfone ${micEnabled ? 'ligado' : 'desligado'}`}
      >
        <MicGlyph color={micEnabled ? INK : WHITE} />
        {micEnabled && <AnimatedSoundwave level={audioLevel} />}
      </Pill>

      {/* Camera */}
      <Pill
        bg={cameraEnabled ? WHITE : DUNE}
        iconColor={cameraEnabled ? INK : WHITE}
        showSlash={!cameraEnabled}
        label="Câmera"
        onPress={onToggleCamera}
        haptic="selection"
        accessibilityState={{ selected: cameraEnabled }}
        accessibilityLabel={`Câmera ${cameraEnabled ? 'ligada' : 'desligada'}`}
      >
        <RecordVideoGlyph color={cameraEnabled ? INK : WHITE} size={24} />
        {cameraEnabled && <View style={styles.recordDot} />}
      </Pill>

      {/* Flip */}
      <Pill
        bg={WHITE}
        iconColor={INK}
        label="Girar"
        onPress={onSwitchCamera}
        disabled={!canFlipCamera}
        haptic="light"
        accessibilityLabel="Girar câmera"
      >
        <CameraFlipGlyph color={INK} />
      </Pill>

      {/* Exit */}
      <Pill
        bg={TENN}
        iconColor={WHITE}
        label="Sair"
        onPress={onEnd}
        haptic="medium"
        accessibilityLabel="Encerrar chamada"
      >
        <PhoneHangupGlyph color={WHITE} />
      </Pill>
    </View>
  );
}

interface PillProps {
  bg: string;
  iconColor: string;
  label: string;
  onPress: () => void;
  disabled?: boolean;
  showSlash?: boolean;
  /** Haptic style fired on tap. Defaults to a soft selection tick. */
  haptic?: HapticKind;
  accessibilityLabel?: string;
  accessibilityState?: { selected?: boolean; disabled?: boolean };
  children: React.ReactNode;
}

function Pill({
  bg,
  label,
  onPress,
  disabled,
  showSlash,
  haptic = 'selection',
  accessibilityLabel,
  accessibilityState,
  children,
}: PillProps) {
  // Fire haptic before invoking the consumer's handler — keeps the tap feel
  // crisp even if onPress kicks off async Agora work that briefly blocks.
  const handlePress = React.useCallback(() => {
    fireHaptic(haptic);
    onPress();
  }, [haptic, onPress]);
  return (
    <View style={styles.col}>
      <TouchableOpacity
        style={[
          styles.pill,
          { backgroundColor: bg },
          disabled && { opacity: 0.5 },
        ]}
        onPress={handlePress}
        disabled={disabled}
        activeOpacity={0.85}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel ?? label}
        accessibilityState={{ disabled, ...accessibilityState }}
      >
        {/* Icon + adornment (soundwave / record dot) sit side-by-side in a
            row — the pill itself is `flexDirection: 'row'` so their natural
            inline order is preserved instead of being forced onto two
            stacked lines. The slash, when shown, overlays the whole row. */}
        <View style={styles.iconRow}>
          {children}
        </View>
        {showSlash && (
          // The shared `DiagonalSlash` has its OWN `position: 'absolute',
          // top: -1, left: -1` — designed to sit on top of a 24×24 icon
          // anchor (matches the lobby pill, where it overlays the glyph).
          // We need that same 24×24 anchor here, perfectly centered in the
          // pill, otherwise the slash sticks to the pill's top-left corner.
          <View pointerEvents="none" style={styles.slashOverlay}>
            <View style={styles.slashAnchor}>
              <DiagonalSlash />
            </View>
          </View>
        )}
      </TouchableOpacity>
      <Text style={styles.label}>{label}</Text>
    </View>
  );
}

/** Tenn-orange "Sair" pill uses a phone-hangup glyph (rotated 135°). The
 *  Figma export only ships the icon for this state — keeping it inline so
 *  the rest of the app doesn't accidentally pick it up. */
function PhoneHangupGlyph({ color }: { color: string }) {
  return (
    <Svg width={24} height={24} viewBox="0 0 24 24" fill="none">
      <Path
        d="M21.97 18.33a2.54 2.54 0 0 1-.25 1.09 4.18 4.18 0 0 1-.68.99 4.5 4.5 0 0 1-1.64 1.18c-.6.27-1.25.41-1.91.41a8.13 8.13 0 0 1-3.2-.72A17.4 17.4 0 0 1 11 19.32a28.4 28.4 0 0 1-3.23-3.06A28.7 28.7 0 0 1 4.7 13.04a17 17 0 0 1-1.93-3.27A8.36 8.36 0 0 1 2.05 6.6c0-.66.12-1.29.35-1.87.23-.59.6-1.13 1.12-1.6.62-.59 1.31-.88 2.04-.88.27 0 .55.06.79.18.26.12.49.3.66.55l2.31 3.26c.18.25.31.48.4.7.09.21.14.42.14.61 0 .24-.07.48-.21.71-.13.23-.32.47-.56.71l-.76.79a.5.5 0 0 0-.16.4c0 .08.01.15.03.23.03.09.05.16.07.22.18.32.48.74.92 1.25.45.51.93 1.04 1.45 1.56.54.52 1.07 1 1.59 1.45.51.43.94.74 1.27.92.05.02.11.04.19.07.08.03.16.04.25.04.16 0 .29-.06.4-.17l.76-.75c.25-.25.49-.44.72-.56.23-.14.46-.21.71-.21.19 0 .39.04.61.13.22.09.45.22.7.39l3.31 2.35c.25.18.42.39.53.63.1.25.16.49.16.77Z"
        fill={color}
        transform="rotate(135 12 12)"
      />
    </Svg>
  );
}

const styles = StyleSheet.create({
  glass: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    padding: 16,
    borderRadius: 24,
    backgroundColor: GLASS_BG,
  },
  col: {
    flex: 1,
    alignItems: 'center',
    gap: 8,
  },
  pill: {
    width: '100%',
    height: 48,
    borderRadius: 32,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 12,
    paddingVertical: 12,
    overflow: 'hidden',
    position: 'relative',
  },
  // Inline row so icon + soundwave / record-dot stay on a single line.
  iconRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  slashOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // 24×24 anchor centered in the pill — DiagonalSlash positions itself
  // at top:-1, left:-1 relative to this box (so the stroke extends 1px
  // past the icon on the top-left, matching Figma).
  slashAnchor: {
    width: 24,
    height: 24,
  },
  recordDot: {
    width: 6,
    height: 6,
    borderRadius: 4,
    backgroundColor: BLUE_HORIZON,
  },
  label: {
    color: WHITE,
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6,
    textAlign: 'center',
  },
});
